import { useEffect } from 'react'
import { ApolloError, gql, useLazyQuery } from '@apollo/client'
import { getContentfulClient, getWebClient } from '@/services/ApolloClient'
import { GET_FEATURED_PROJECT_BY_ID_QUERY } from '@/services/CmsService/FeaturedProject'
import { useUserRegion } from '@/services/UserRegionService'
import { FeaturedProject, GetFeaturedProjectByIdQuery } from '@/types/codegen-contentful'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'

const GET_CONTENT_BY_PROJECT_SLUG = gql`
  query getContentByProjectSlug($slug: String!) {
    project(slug: $slug) {
      id
      title {
        __typename
        ... on Node {
          id
        }
      }
    }
  }
`

interface UseFeaturedProjectOptions {
  featuredProjectId: string
  preview?: boolean
}

interface FederatedContent {
  id: string
  __typename: string
}

interface UseFeaturedProjectResult {
  featuredProject: FeaturedProject | undefined
  federatedContent: FederatedContent | undefined
  loading: boolean
  error: ApolloError | undefined
  called: boolean
  refetch: () => void
  fetchFeaturedProject: () => void
}

export const useFeaturedProject = ({
  featuredProjectId,
  preview = false,
}: UseFeaturedProjectOptions): UseFeaturedProjectResult => {
  const { locale } = useLocale()
  const { region } = useUserRegion()

  const contentfulClient = getContentfulClient({ locale, preview })
  const federationClient = getWebClient({ locale })

  const [getFeaturedProjectById, { data, loading, error, called }] = useLazyQuery<GetFeaturedProjectByIdQuery>(
    GET_FEATURED_PROJECT_BY_ID_QUERY,
    {
      client: contentfulClient,
      variables: { id: featuredProjectId, region, locale, preview },
      errorPolicy: 'all',
      onError: (error) => {
        logger().error(
          'useFeaturedProject query failed',
          {
            featuredProjectId,
            locale,
            region,
            preview,
            errorMessage: error.message,
            errorName: error.name,
            graphQLErrors: error.graphQLErrors?.map((e) => ({
              message: e.message,
              path: e.path,
              extensions: e.extensions,
            })),
            networkError: error.networkError
              ? {
                  message: error.networkError.message,
                  name: error.networkError.name,
                }
              : null,
          },
          error,
        )
      },
      onCompleted: (res) => {
        const items = res?.featuredProjectCollection?.items
        if (!Array.isArray(items) || items.length === 0 || !items[0]) {
          logger().warn('useFeaturedProject returned no items', {
            res,
            featuredProjectId,
            locale,
            region,
            preview,
            itemsLength: items?.length,
          })
        }
      },
    },
  )

  const [getFederatedContent, { data: federatedData, loading: federatedLoading, error: federatedError }] = useLazyQuery(
    GET_CONTENT_BY_PROJECT_SLUG,
    {
      client: federationClient,
      errorPolicy: 'all',
      onError: (error) => {
        logger().error(
          'useFeaturedProject federation query failed',
          {
            projectSlug: data?.featuredProjectCollection?.items?.[0]?.projectSlug,
            errorMessage: error.message,
            errorName: error.name,
          },
          error,
        )
      },
    },
  )

  const fetchFeaturedProject = () => {
    try {
      void getFeaturedProjectById({ variables: { id: featuredProjectId, region, locale, preview } })
    } catch (error) {
      logger().error(
        'useFeaturedProject query invocation error',
        {
          featuredProjectId,
          locale,
          region,
          preview,
        },
        error,
      )
    }
  }

  // Fetch federated content when we have the projectSlug
  useEffect(() => {
    const projectSlug = data?.featuredProjectCollection?.items?.[0]?.projectSlug
    if (projectSlug) {
      void getFederatedContent({ variables: { slug: projectSlug } })
    }
  }, [data, getFederatedContent])

  const featuredProject = data?.featuredProjectCollection?.items?.find(Boolean) as FeaturedProject | undefined

  const federatedContent: FederatedContent | undefined = federatedData?.project?.title
    ? {
        id: federatedData.project.title.id,
        __typename: federatedData.project.title.__typename,
      }
    : undefined

  return {
    featuredProject,
    federatedContent,
    loading: loading || federatedLoading,
    error: error || federatedError,
    called,
    refetch: () => {
      void getFeaturedProjectById({ variables: { id: featuredProjectId, region, locale, preview } })
    },
    fetchFeaturedProject,
  }
}
